import React from 'react'
import { useTranslation } from 'react-i18next'
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { TrendingDown } from 'lucide-react'
import { useUnits } from '../../../contexts/UnitsContext'
import { useTheme } from '../../../contexts/ThemeContext'

const CustomTooltip = ({ active, payload, label, unit }) => {
  if (!active || !payload || !payload.length) return null
  return (
    <div className="bg-gray-900 dark:bg-gray-800 text-white px-3 py-2 rounded-lg shadow-lg border border-app-primary-500/30 text-sm">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="font-mono font-bold text-app-primary-400">{Number(payload[0].value).toFixed(1)} {unit}</p>
    </div>
  )
}

export function MeasurementChart({ measurements, selectedMetric }) {
  const { t } = useTranslation()
  const { lengthUnit, displayValueLength } = useUnits()
  const { primaryHsl } = useTheme()
  const primary500 = primaryHsl(500)

  const isBodyFat = selectedMetric === 'body_fat'
  const unit = isBodyFat ? '%' : lengthUnit
  const metricLabel = isBodyFat
    ? t('phoenixTracker.measurements.bodyFat')
    : t(`phoenixTracker.measurements.${selectedMetric}`)

  // Filter to only entries that have a value for the selected metric
  // Sort ascending for the chart (measurements come in desc order)
  const chartData = [...measurements]
    .filter((m) => m[selectedMetric] != null)
    .reverse()
    .map((m) => ({
      date: new Date(m.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      value: isBodyFat ? Number(m[selectedMetric]) : displayValueLength(Number(m[selectedMetric])),
    }))

  // Need at least 2 points to draw a meaningful line
  if (chartData.length < 2) return null

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingDown className="w-4 h-4 text-app-primary-500" />
          {t('phoenixTracker.measurements.trend')}
          <span className="text-sm font-normal text-muted-foreground">
            · {metricLabel}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div dir="ltr" className="w-full">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="measurementGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={primary500} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={primary500} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fontFamily: '"JetBrains Mono", monospace' }}
                stroke="#6B7280"
              />
              <YAxis
                domain={['dataMin - 1', 'dataMax + 1']}
                tick={{ fontSize: 11, fontFamily: '"JetBrains Mono", monospace' }}
                stroke="#6B7280"
              />
              <Tooltip content={<CustomTooltip unit={unit} />} />
              <Area
                type="monotone"
                dataKey="value"
                stroke={primary500}
                strokeWidth={2}
                fill="url(#measurementGradient)"
                dot={{ fill: primary500, strokeWidth: 0, r: 3 }}
                activeDot={{ r: 5, fill: primary500, stroke: '#fff', strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}
